import { Link } from 'react-router-dom';
import { useProjects } from '../../api/projects.js';
import { Alert, Spinner } from '../../components/ui.js';
import { ProjectProgress } from '../../components/ProjectDashboard.js';
import { ProjectStatusBadge } from '../../status.js';

/** A small pill flagging why a project needs attention, in the staff list. */
function AttentionBadge({ kind }: { kind: 'approval' | 'message' }) {
  const label = kind === 'approval' ? 'Needs approval' : 'New message';
  return (
    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
      {label}
    </span>
  );
}

/** Staff view: every founder's project, the developer's work queue. */
export function SubmissionsPage() {
  const { data: projects, isPending, isError } = useProjects();

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-title text-3xl tracking-wide">Projects</h1>
        <p className="opacity-70">
          Every project across all founders. Open one to review, build, and supervise its console.
        </p>
      </div>

      {isPending ? (
        <Spinner />
      ) : isError ? (
        <Alert>Could not load submissions.</Alert>
      ) : projects.length === 0 ? (
        <p className="opacity-70">No projects have been created yet.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {projects.map((project) => {
            const attention = project.needsApproval || project.unreadMessages;
            return (
              <li
                key={project.id}
                className={`rounded-lg bg-white p-4 shadow-sm ring-1 transition hover:ring-primary/30 ${
                  attention ? 'ring-amber-400/60' : 'ring-secondary/5'
                }`}
              >
                <Link
                  to={`/console/projects/${project.id}`}
                  className="flex items-center justify-between gap-4"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{project.name}</span>
                      {project.needsApproval ? <AttentionBadge kind="approval" /> : null}
                      {project.unreadMessages ? <AttentionBadge kind="message" /> : null}
                    </div>
                    <div className="truncate text-sm opacity-60">{project.ownerEmail}</div>
                  </div>
                  <ProjectStatusBadge status={project.status} />
                </Link>
                <div className="mt-3">
                  <ProjectProgress projectId={project.id} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
